import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Chip,
  Paper,
  Grid,
  Alert,
  IconButton,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

export * from '../../ui/ImageModal/ImageModal.styles';

/** Palette aligned with AddInventoryPanel (registro de inventario). */
export const PRODUCT_DRAWER_COLORS = {
  primary: '#1a2a67',
  secondary: '#00bcd4',
  accent: '#6b5de6',
  success: '#05c8a5',
  lightGray: '#f5f7fa',
  darkGray: '#555',
  violetSoft: '#9333ea',
  blueRoyal: '#2563eb',
  cyanButton: '#06b6d4',
  cyanLight: '#22d3ee',
  tealMedium: '#14b8a6',
  springGreen: '#22c55e',
  greenButton: '#10b981',
} as const;

export const PRODUCT_STEPS = [
  {
    id: 1,
    name: 'Información Básica',
    color: PRODUCT_DRAWER_COLORS.violetSoft,
    completedBg: '#f3e8ff',
    completedFg: '#6b21a8',
  },
  {
    id: 2,
    name: 'Categoría',
    color: PRODUCT_DRAWER_COLORS.blueRoyal,
    completedBg: '#dbeafe',
    completedFg: '#1e40af',
  },
  {
    id: 3,
    name: 'Propiedades',
    color: PRODUCT_DRAWER_COLORS.cyanButton,
    completedBg: '#cffafe',
    completedFg: '#0e7490',
  },
  {
    id: 4,
    name: 'Precios',
    color: PRODUCT_DRAWER_COLORS.tealMedium,
    completedBg: '#ccfbf1',
    completedFg: '#0f766e',
  },
  {
    id: 5,
    name: 'Proveedor',
    color: PRODUCT_DRAWER_COLORS.greenButton,
    completedBg: '#d1fae5',
    completedFg: '#065f46',
  },
] as const;

export const PANEL_FOOTER_GRADIENT = 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)';
export const PANEL_FOOTER_GRADIENT_HOVER = 'linear-gradient(135deg, #0891b2 0%, #059669 100%)';

/** Same open/close motion as AddInventoryPanel. */
export const PanelOverlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1201,
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  willChange: 'opacity',
  '&.open': {
    opacity: 1,
    pointerEvents: 'auto',
    transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&.closed': {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

export const PanelContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  width: '100%',
  maxWidth: '900px',
  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  zIndex: 1202,
  // Keep panel fully opaque; only overlay fades (siblings, not nested).
  opacity: 1,
  transform: 'translateX(100%)',
  willChange: 'transform',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'auto',
  transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&.open': {
    transform: 'translateX(0)',
    transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&.closed': {
    transform: 'translateX(100%)',
    pointerEvents: 'none',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

// Main container — same continuous paper surface as AddInventoryPanel
export const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
}));

// Header (matches AddInventoryPanel)
export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

export const DrawerTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '1.125rem',
  color: theme.palette.text.primary,
  margin: 0,
  lineHeight: 1.4,
}));

export const CloseButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minWidth: 'auto',
  padding: '4px',
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
  '&:focus': {
    outline: 'none',
  },
}));

// Progress bar (matches AddInventoryPanel ProgressBar)
export const ProgressContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

export const ProgressBar = styled(Box)(({ theme }) => ({
  height: '10px',
  width: '100%',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.12) : '#e5e7eb',
  borderRadius: '10px',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
}));

export const ProgressFill = styled(Box)(({ theme }) => ({
  height: '100%',
  background: 'linear-gradient(to right, #9333ea, #3b82f6, #10b981)',
  transition: 'all 0.5s ease-in-out',
  borderRadius: theme.spacing(1),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    animation: 'productDrawerProgressShimmer 2s infinite',
  },
  '@keyframes productDrawerProgressShimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
}));

export const ProgressText = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(0.5),
  fontSize: '13px',
  letterSpacing: '0.01em',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  '& span:last-of-type': {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
}));

// Step indicator (matches AddInventoryPanel StepIndicators)
export const StepIndicatorContainer = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

export const StepIndicator = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-around',
  width: '100%',
}));

export const StepItem = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  flex: 1,
  maxWidth: '120px',
}));

export const StepCircle = styled(Box)<{
  isActive: boolean;
  isCompleted: boolean;
  color: string;
  completedBg: string;
  completedFg: string;
}>(({ theme, isActive, isCompleted, color, completedBg, completedFg }) => {
  const inactiveBg = theme.palette.mode === 'dark' ? theme.palette.divider : '#e5e7eb';
  const inactiveFg = theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#6b7280';
  const softBg = theme.palette.mode === 'dark' ? alpha(color, 0.28) : completedBg;
  const softFg = theme.palette.mode === 'dark' ? alpha(completedFg, 0.95) : completedFg;

  return {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease-in-out',
    position: 'relative',
    zIndex: 2,
    backgroundColor: isActive ? color : isCompleted ? softBg : inactiveBg,
    color: isActive ? '#ffffff' : isCompleted ? softFg : inactiveFg,
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '& svg': {
      fontSize: 22,
      width: 22,
      height: 22,
      transition: 'all 0.3s ease-in-out',
    },
  };
});

export const StepLabel = styled(Typography)<{ isActive: boolean; isCompleted: boolean }>(
  ({ theme, isActive, isCompleted }) => ({
    fontSize: '13px',
    textAlign: 'center',
    lineHeight: 1.4,
    transition: 'all 0.3s ease-in-out',
    maxWidth: '120px',
    wordWrap: 'break-word',
    fontWeight: isActive ? 600 : 500,
    color:
      isActive || isCompleted
        ? theme.palette.text.primary
        : theme.palette.mode === 'dark'
          ? theme.palette.text.secondary
          : '#6b7280',
  }),
);

export const StepConnector = styled(Box)<{
  isCompleted: boolean;
  fromColor: string;
  toColor: string;
}>(({ theme, isCompleted, fromColor, toColor }) => ({
  position: 'absolute',
  top: '28px',
  left: '50%',
  width: '100%',
  height: '2px',
  zIndex: 1,
  background: isCompleted
    ? `linear-gradient(to right, ${fromColor}, ${toColor})`
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : '#e5e7eb',
}));

// Step content
export const StepContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
}));

export const StepCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: theme.spacing(1.5),
  ...(theme.palette.mode === 'dark'
    ? {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      }),
}));

/** Banner de paso — mismo lenguaje visual que AddInventoryPanel StepHeader */
export const StepCardHeader = styled(Box)<{ color: string }>(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.15 : 0.1),
  border: `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    borderRadius: theme.spacing(1),
    zIndex: 0,
  },
}));

export const StepCardHeaderBorder = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: '8px',
  alignSelf: 'stretch',
  marginRight: theme.spacing(2),
  borderRadius: theme.spacing(0.5),
  backgroundColor: color,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
}));

export const StepCardHeaderIcon = styled(Box)<{ color: string }>(({ theme, color }) => ({
  marginRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  zIndex: 1,
  '& svg': {
    fontSize: 24,
    width: 24,
    height: 24,
    transition: 'transform 0.3s ease-in-out',
  },
  '&:hover svg': {
    transform: 'scale(1.1)',
  },
}));

export const StepCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  position: 'relative',
  zIndex: 1,
  transition: 'all 0.3s ease-in-out',
}));

export const StepCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

export const FormGrid = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(3),
}));

export const FormField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
  },
}));

export const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
  },
}));

// Image upload
export const ImageUploadContainer = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '256px',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : '#f9fafb',
  position: 'relative',
}));

export const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}));

export const ImageActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(0.5),
  right: theme.spacing(0.5),
  display: 'flex',
  gap: theme.spacing(0.5),
}));

export const ImageActionButton = styled(IconButton)<{
  bgColor?: string;
  /** Degradado (catálogo); si se define, prevalece sobre bgColor */
  gradient?: string;
}>(({ bgColor, gradient }) => ({
  ...(gradient
    ? { background: gradient, backgroundColor: 'transparent' }
    : { backgroundColor: bgColor ?? 'rgba(55, 65, 81, 0.85)' }),
  color: 'white',
  '&:hover': {
    ...(gradient ? { filter: 'brightness(0.94)' } : { backgroundColor: bgColor, opacity: 0.88 }),
  },
}));

export const UploadButton = styled(Button)<{ gradient: string }>(({ theme, gradient }) => ({
  background: gradient,
  color: 'white',
  padding: theme.spacing(1, 2),
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    background: gradient,
    opacity: 0.9,
  },
}));

export const UploadText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

// Tags
export const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: `${PRODUCT_DRAWER_COLORS.blueRoyal}20`,
  color: PRODUCT_DRAWER_COLORS.blueRoyal,
  '& .MuiChip-deleteIcon': {
    color: PRODUCT_DRAWER_COLORS.blueRoyal,
  },
}));

export const TagInput = styled('input')(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.success.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.2)}`,
  },
}));

export const TagErrorMessage = styled(Box)(({ theme }) => ({
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(0.5),
  fontWeight: 500,
}));

export const AvailableTagsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

export const AvailableTagsLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

export const AvailableTagsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

export const AvailableTagButton = styled(Button)<{ isSelected: boolean; tagColor?: string }>(({
  theme,
  isSelected,
  tagColor,
}) => {
  // Helper function to darken a hex color
  const darkenColor = (hex: string, amount: number = 0.2) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * amount * 100);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  const baseColor = tagColor || PRODUCT_DRAWER_COLORS.blueRoyal;
  const hoverColor = darkenColor(baseColor, 0.3);

  return {
    padding: theme.spacing(0.5, 1),
    borderRadius: '16px',
    fontSize: '0.875rem',
    textTransform: 'none',
    backgroundColor: isSelected ? `${baseColor}20` : `${baseColor}15`,
    color: isSelected ? baseColor : baseColor,
    border: `1px solid ${baseColor}30`,
    '&:hover': {
      backgroundColor: isSelected ? `${baseColor}30` : `${hoverColor}20`,
      borderColor: hoverColor,
      color: hoverColor,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  };
});

// Footer
export const FooterContainer = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

export const FooterActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const FooterLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const FooterRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
}));

export const PrimaryActionButton = styled(ActionButton)<{ gradient?: string }>(
  ({ theme, gradient }) => ({
    background: gradient || PANEL_FOOTER_GRADIENT,
    color: theme.palette.common.white,
    minWidth: '100px',
    border: 'none',
    '&:hover': {
      background: gradient ? gradient : PANEL_FOOTER_GRADIENT_HOVER,
      border: 'none',
      filter: gradient ? 'brightness(0.95)' : undefined,
    },
    '&:disabled': {
      opacity: 0.5,
      background: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      border: 'none',
    },
  }),
);

export const SecondaryActionButton = styled(ActionButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.text.secondary,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

// Loading overlay
export const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
}));

// Error alert
export const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
}));

// Helper text
export const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));
